/* eslint-disable no-nested-ternary */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  MdLocationOn,
  MdAccessTime,
  MdLocalShipping,
  MdDownload,
  MdUploadFile,
  MdDelete,
} from 'react-icons/md';
import { FormPage } from '../../tailwindComponents/FormPage';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { Autocomplete } from '../../tailwindComponents/Autocomplete';
import { Dropzone } from '../../tailwindComponents/Dropzone';
import { Button } from '../../tailwindComponents/Button';
import { Checkbox } from '../../tailwindComponents/Checkbox';
import {
  LoadSizeMatrixFields,
  DEFAULT_PALLET_RATES,
  DEFAULT_WEIGHT_RATES,
} from '../AddTariff/LoadSizeMatrixFields';
import { useAuthHttp } from '../../hooks';
import {
  POSTCODE_RATECARD_CREATE,
  DAILY_RATECARD_CREATE,
  MILEAGE_RATECARD_UPDATE,
  ADDITIONAL_FTL_RATECARD_CREATE,
  RATECARD_GET,
  HERE_APIKEY,
} from '../../constants';
import {
  MAXIMUM_FUEL_SURCHARGE_PERCENTAGE,
  OFF_ROUTE_DISTANCE_UNIT,
} from '../../constants/ratecards';
import { fleetMapping } from '../PriceMaster/types';

const CURRENCY_OPTIONS = [
  { label: 'GBP', value: 'GBP' },
  { label: 'EUR', value: 'EUR' },
  { label: 'USD', value: 'USD' },
];

const TARIFF_TYPE_OPTIONS = [
  { label: 'Postcode RateCard', value: 'Postcode' },
  { label: 'Additional Load Charge', value: 'Additional FTL' },
  { label: 'Day Rate', value: 'Day Rate' },
  { label: 'Price Per Mile', value: 'Price Per Mile' },
];

// Base schema for all tariff types
const baseSchema = Yup.object().shape({
  tariffType: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .required('Tariff type is required'),
  vehicleType: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .required('Vehicle type is required'),
  transportBaseAddress: Yup.string()
    .required('Transport base address is required')
    .min(3, 'Transport base address must be at least 3 characters'),
  location: Yup.object().shape({
    lon: Yup.number(),
    lat: Yup.number(),
  }),
  currency: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .required('Currency is required'),
});

const hasRealCoordinates = (location?: { lat?: number; lon?: number }) => {
  const lat = location?.lat ?? 0;
  const lon = location?.lon ?? 0;
  return (
    Math.abs(lat) <= 90 && Math.abs(lon) <= 180 && !(lat === 0 && lon === 0)
  );
};

export type TariffFormValues = {
  tariffType: { label: string; value: string };
  vehicleType: { label: string; value: string };
  transportBaseAddress: string;
  location: { lon: number; lat: number };
  locationAddressLabel?: string;
  currency: { label: string; value: string };
  // Day Rate fields
  chargeForEightHourDay?: number;
  hourlyChargeOverEightHours?: number;
  hourlyChargeOverTenHours?: number;
  // Additional Load Charge fields
  rateMode?: 'pallet' | 'weight';
  palletRates?: { pallets: number; price?: number }[];
  weightRates?: { minWeight: number; maxWeight?: number | null; price?: number }[];
  maxOffRouteDistance?: number;
  chargePerAdditionalDrop?: number;
  standardIncludedLoadingMinutes?: number;
  standardIncludedUnloadingMinutes?: number;
  chargePerAdditionalHour?: number;
  chargePerOvertimeHour?: number;
  // Price Per Mile fields
  pricePerMile?: number;
  baseCharge?: number | null;
  fuelSurchargePercentage?: number | null;
  minimumMiles?: number | null;
  maximumMiles?: number | null;
  maximumLoadKg?: number;
  willCollectFromAnywhere?: boolean;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  // Upload file for Postcode
  uploadFile?: File;
};

export function EditTariff() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const authHttp = useAuthHttp();
  const authHttpFormData = useAuthHttp('multipart/form-data');

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [addressOptions, setAddressOptions] = useState<
    { id: string; label: string; city?: string; country?: string }[]
  >([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<
    { message: string; row: number }[]
  >([]);

  // Get URL params for pre-filling
  const tariffType = searchParams.get('type') || '';
  const vehicleType = searchParams.get('vehicleType') || '';
  const transportBaseAddress = searchParams.get('address') || '';

  const tariffLabel = TARIFF_TYPE_OPTIONS.find((t) => t.value === tariffType)?.label || 'Transport Tariff';
  const vehicleLabel = fleetMapping[vehicleType as keyof typeof fleetMapping] || '';
  const editTitle = vehicleLabel
    ? `Edit ${tariffLabel} for ${vehicleLabel}`
    : `Edit ${tariffLabel}`;

  useDocumentTitle(`FLOX - ${editTitle}`);

  // Build vehicle type options from fleetMapping
  const vehicleTypeOptions = useMemo(
    () =>
      Object.entries(fleetMapping).map(([value, label]) => ({
        label,
        value,
      })),
    []
  );

  const defaultValues: TariffFormValues = {
    tariffType:
      TARIFF_TYPE_OPTIONS.find((t) => t.value === tariffType) ||
      TARIFF_TYPE_OPTIONS[0],
    vehicleType:
      vehicleTypeOptions.find((v) => v.value === vehicleType) ||
      vehicleTypeOptions[0],
    transportBaseAddress,
    location: { lon: 0, lat: 0 },
    locationAddressLabel: undefined,
    currency: CURRENCY_OPTIONS[0],
    // Day Rate defaults
    chargeForEightHourDay: undefined,
    hourlyChargeOverEightHours: undefined,
    hourlyChargeOverTenHours: undefined,
    // Additional Load Charge defaults
    rateMode: 'pallet',
    palletRates: DEFAULT_PALLET_RATES,
    weightRates: DEFAULT_WEIGHT_RATES,
    maxOffRouteDistance: undefined,
    chargePerAdditionalDrop: undefined,
    standardIncludedLoadingMinutes: undefined,
    standardIncludedUnloadingMinutes: undefined,
    chargePerAdditionalHour: undefined,
    chargePerOvertimeHour: undefined,
    // Price Per Mile defaults
    pricePerMile: undefined,
    baseCharge: undefined,
    fuelSurchargePercentage: undefined,
    minimumMiles: undefined,
    maximumMiles: undefined,
    maximumLoadKg: undefined,
    willCollectFromAnywhere: false,
    effectiveFrom: undefined,
    effectiveTo: undefined,
    // Upload file
    uploadFile: undefined,
  };

  const methods = useForm<TariffFormValues>({
    shouldUnregister: false,
    resolver: yupResolver(baseSchema),
    mode: 'onChange',
    defaultValues,
  });

  const { control, setValue, watch, setError, clearErrors } = methods;
  const transportBaseLocationError = methods.formState.errors.location?.message;

  const watchTariffType = watch('tariffType');
  const watchVehicleType = watch('vehicleType');

  // Clear validation errors when tariff type changes
  useEffect(() => {
    if (watchTariffType?.value) {
      clearErrors();
    }
  }, [watchTariffType?.value, clearErrors]);

  // Map frontend tariff type to backend RateCardTypes
  const getRateCardType = (type: string) => {
    switch (type) {
      case 'Day Rate':
        return 'daily';
      case 'Additional FTL':
        return 'additionalLoad';
      case 'Postcode':
        return 'postcode';
      case 'Price Per Mile':
        return 'mileage';
      default:
        return 'postcode';
    }
  };

  // Fetch existing tariff data
  useEffect(() => {
    const fetchTariffData = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      try {
        const rateCardType = getRateCardType(tariffType);
        const { data } = await authHttp.get(
          `${RATECARD_GET}?type=${rateCardType}&id=${id}`
        );

        const rc = data?.dailyRateCard || data?.postcodeRateCard || data?.additionalLoadRateCard || data?.mileageRateCard;
        if (rc) {

          // Set currency
          const currencyOption = CURRENCY_OPTIONS.find(
            (c) => c.value === rc.chargeCurrency
          );
          if (currencyOption) setValue('currency', currencyOption);

          if (rc.transportBaseLocation) {
            setValue('location', {
              lat: rc.transportBaseLocation.lat,
              lon: rc.transportBaseLocation.lon,
            });
            setValue('locationAddressLabel', rc.transportBaseAddress);
          }

          // Set Price Per Mile fields
          if (tariffType === 'Price Per Mile') {
            if (rc.pricePerMile != null) setValue('pricePerMile', rc.pricePerMile);
            if (rc.baseCharge > 0) setValue('baseCharge', rc.baseCharge);
            if (rc.fuelSurchargePercentage > 0)
              setValue('fuelSurchargePercentage', rc.fuelSurchargePercentage);
            if (rc.minimumMiles != null) setValue('minimumMiles', rc.minimumMiles);
            if (rc.maximumMiles != null) setValue('maximumMiles', rc.maximumMiles);
            if (rc.maximumLoadKg != null) setValue('maximumLoadKg', rc.maximumLoadKg);
            setValue(
              'willCollectFromAnywhere',
              rc.willCollectFromAnywhere === true
            );
            if (rc.effectiveFrom != null) setValue('effectiveFrom', rc.effectiveFrom);
            if (rc.effectiveTo != null) setValue('effectiveTo', rc.effectiveTo);
          }

          // Set Additional Load Charge fields
          if (tariffType === 'Additional FTL') {
            if (rc.maxOffRouteDistance != null)
              setValue('maxOffRouteDistance', rc.maxOffRouteDistance);
            if (rc.chargePerAdditionalDrop != null)
              setValue('chargePerAdditionalDrop', rc.chargePerAdditionalDrop);
            if (rc.standardIncludedLoadingMinutes != null)
              setValue(
                'standardIncludedLoadingMinutes',
                rc.standardIncludedLoadingMinutes
              );
            if (rc.standardIncludedUnloadingMinutes != null)
              setValue(
                'standardIncludedUnloadingMinutes',
                rc.standardIncludedUnloadingMinutes
              );
            if (rc.chargePerAdditionalHour != null)
              setValue('chargePerAdditionalHour', rc.chargePerAdditionalHour);
            if (rc.chargePerOvertimeHour != null)
              setValue('chargePerOvertimeHour', rc.chargePerOvertimeHour);
            if (rc.palletRates?.length > 0) {
              setValue('rateMode', 'pallet');
              setValue(
                'palletRates',
                rc.palletRates.map((r: any) => ({
                  pallets: r.pallets,
                  price: r.price,
                }))
              );
            } else if (rc.weightRates?.length > 0) {
              setValue('rateMode', 'weight');
              setValue(
                'weightRates',
                rc.weightRates.map((r: any) => ({
                  minWeight: r.minWeight,
                  maxWeight: r.maxWeight,
                  price: r.price,
                }))
              );
            }

            // Set currency from additionalChargeCurrency
            const addlCurrency = CURRENCY_OPTIONS.find(
              (c) => c.value === rc.additionalChargeCurrency
            );
            if (addlCurrency) setValue('currency', addlCurrency);
          }

          // Set Day Rate fields
          if (tariffType === 'Day Rate') {
            if (rc.chargeForEightHourDay != null)
              setValue('chargeForEightHourDay', rc.chargeForEightHourDay);
            if (rc.hourlyChargeOverEightHours != null)
              setValue(
                'hourlyChargeOverEightHours',
                rc.hourlyChargeOverEightHours
              );
            if (rc.hourlyChargeOverTehnHours != null)
              setValue(
                'hourlyChargeOverTenHours',
                rc.hourlyChargeOverTehnHours
              );
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching tariff data:', error);
        setIsLoading(false);
      }
    };

    fetchTariffData();
  }, [id]);

  // Fetch address suggestions
  const handleFetchSuggestions = useCallback((input: string) => {
    if (input.length < 3) {
      setAddressOptions([]);
      return;
    }

    fetch(
      `https://autosuggest.search.hereapi.com/v1/autosuggest?apiKey=${HERE_APIKEY}&q=${input}&at=52.93175,12.77165`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.items) {
          const items = data.items.map(
            (item: {
              id: string;
              address: { label: string; city?: string; countryName?: string };
            }) => ({
              id: item.id,
              label: item.address.label,
              city: item.address.city,
              country: item.address.countryName,
            })
          );
          setAddressOptions(items);
        }
      })
      .catch((error) => {
        console.error('Error fetching suggestions:', error);
      });
  }, []);

  // Handle address selection
  const handleAddressSelect = useCallback(
    async (option: { id: string; label: string } | null) => {
      if (!option) return;
      setValue('transportBaseAddress', option.label);
      try {
        const response = await fetch(
          `https://lookup.search.hereapi.com/v1/lookup?id=${option.id}&apiKey=${HERE_APIKEY}`
        );
        const data = await response.json();
        if (data?.position) {
          const { lat, lng: lon } = data.position;
          setValue('locationAddressLabel', option.label);
          setValue('location', { lat, lon }, { shouldValidate: true });
        }
      } catch (error) {
        console.error('Error fetching position:', error);
      }
    },
    [setValue]
  );

  // Get API endpoint based on tariff type
  const getApiEndpoint = (pricingType: string) => {
    switch (pricingType) {
      case 'Postcode':
        return POSTCODE_RATECARD_CREATE;
      case 'Day Rate':
        return DAILY_RATECARD_CREATE;
      case 'Additional FTL':
        return ADDITIONAL_FTL_RATECARD_CREATE;
      case 'Price Per Mile':
        return MILEAGE_RATECARD_UPDATE;
      default:
        return POSTCODE_RATECARD_CREATE;
    }
  };

  // File download path for templates
  const fileDownloadPath = useMemo(() => {
    const tariffTypeValue =
      watchTariffType?.value?.toLowerCase().replace(/\s+/g, '_') || 'postcode';
    const vehicleTypeValue =
      watchVehicleType?.value?.toLowerCase().replace(/\s+/g, '_') || '';
    return `${tariffTypeValue}_${vehicleTypeValue}`;
  }, [watchTariffType?.value, watchVehicleType?.value]);

  // Handle form submission
  const onSubmit = async (data: TariffFormValues) => {
    setIsSubmitting(true);
    setHasUploadError(false);
    setUploadErrors([]);

    // Manual validation for tariff-specific fields
    const pricingType = data.tariffType.value;
    let hasValidationErrors = false;

    if (pricingType === 'Day Rate') {
      if (!data.chargeForEightHourDay || data.chargeForEightHourDay <= 0) {
        setError('chargeForEightHourDay', {
          type: 'manual',
          message:
            'Charge for 8 hour day is required and must be greater than 0',
        });
        hasValidationErrors = true;
      }
      if (
        !data.hourlyChargeOverEightHours ||
        data.hourlyChargeOverEightHours <= 0
      ) {
        setError('hourlyChargeOverEightHours', {
          type: 'manual',
          message:
            'Hourly charge over 8 hours is required and must be greater than 0',
        });
        hasValidationErrors = true;
      }
      if (
        !data.hourlyChargeOverTenHours ||
        data.hourlyChargeOverTenHours <= 0
      ) {
        setError('hourlyChargeOverTenHours', {
          type: 'manual',
          message:
            'Hourly charge over 10 hours is required and must be greater than 0',
        });
        hasValidationErrors = true;
      }
    }

    if (pricingType === 'Price Per Mile') {
      if (data.baseCharge != null && data.baseCharge < 0) {
        setError('baseCharge', {
          type: 'manual',
          message: 'Base charge must be 0 or more',
        });
        hasValidationErrors = true;
      }
      if (
        data.fuelSurchargePercentage != null &&
        (data.fuelSurchargePercentage < 0 ||
          data.fuelSurchargePercentage > MAXIMUM_FUEL_SURCHARGE_PERCENTAGE)
      ) {
        setError('fuelSurchargePercentage', {
          type: 'manual',
          message: `Fuel surcharge must be between 0 and ${MAXIMUM_FUEL_SURCHARGE_PERCENTAGE}%`,
        });
        hasValidationErrors = true;
      }
      if (!data.maximumLoadKg || data.maximumLoadKg <= 0) {
        setError('maximumLoadKg', {
          type: 'manual',
          message: 'Maximum load (kg) is required and must be greater than 0',
        });
        hasValidationErrors = true;
      }
      if (
        !hasRealCoordinates(data.location) ||
        data.transportBaseAddress !== data.locationAddressLabel
      ) {
        setError('location', {
          type: 'manual',
          message: 'Pick the transport base address from the suggestions.',
        });
        hasValidationErrors = true;
      }
    }

    if (pricingType === 'Additional FTL') {
      const additionalFields = [
        { key: 'maxOffRouteDistance', label: 'Max distance off-route' },
        { key: 'chargePerAdditionalDrop', label: 'Charge per extra drop' },
        { key: 'standardIncludedLoadingMinutes', label: 'STD loading time' },
        { key: 'standardIncludedUnloadingMinutes', label: 'STD drop off time' },
        { key: 'chargePerAdditionalHour', label: 'Cost per hour' },
        { key: 'chargePerOvertimeHour', label: 'Overtime per hour' },
      ] as const;

      for (const { key, label } of additionalFields) {
        if (!data[key] || (data[key] as number) <= 0) {
          setError(key, {
            type: 'manual',
            message: `${label} is required and must be greater than 0`,
          });
          hasValidationErrors = true;
        }
      }

      const rates =
        data.rateMode === 'pallet' ? data.palletRates : data.weightRates;
      const hasAtLeastOneRate = rates?.some(
        (r) => r.price !== undefined && r.price > 0
      );
      if (!hasAtLeastOneRate) {
        setHasUploadError(true);
        setUploadErrors([
          { message: 'At least one rate must have a price greater than 0', row: 0 },
        ]);
        hasValidationErrors = true;
      }
    }

    if (hasValidationErrors) {
      setIsSubmitting(false);
      return;
    }

    try {
      const endpoint = getApiEndpoint(pricingType);

      let basePayload: any = {
        id,
        vehicleType: data.vehicleType.value,
        transportBaseAddress: data.transportBaseAddress,
        transportBaseLocation: {
          lat: data.location.lat,
          lon: data.location.lon,
        },
        chargeCurrency: data.currency.value,
      };

      if (pricingType === 'Day Rate') {
        const payload = {
          rateCard: {
            ...basePayload,
            chargeForEightHourDay: data.chargeForEightHourDay,
            hourlyChargeOverEightHours: data.hourlyChargeOverEightHours,
            hourlyChargeOverTehnHours: data.hourlyChargeOverTenHours,
          },
        };
        await authHttp.post(endpoint, payload);
      } else if (pricingType === 'Price Per Mile') {
        const payload = {
          rateCard: {
            ...basePayload,
            pricePerMile: data.pricePerMile,
            baseCharge: data.baseCharge ?? 0,
            fuelSurchargePercentage: data.fuelSurchargePercentage ?? 0,
            minimumMiles: data.minimumMiles ?? null,
            maximumMiles: data.maximumMiles ?? null,
            maximumLoadKg: data.maximumLoadKg,
            willCollectFromAnywhere: data.willCollectFromAnywhere ?? false,
            effectiveFrom: data.effectiveFrom || null,
            effectiveTo: data.effectiveTo || null,
          },
        };
        await authHttp.post(endpoint, payload);
      } else if (pricingType === 'Additional FTL') {
        const currency = data.currency.value;
        const palletRates =
          data.rateMode === 'pallet'
            ? (data.palletRates || [])
                .filter((r) => r.price !== undefined && r.price > 0)
                .map((r) => ({
                  pallets: r.pallets,
                  price: r.price,
                  currency,
                }))
            : [];
        const weightRates =
          data.rateMode === 'weight'
            ? (data.weightRates || [])
                .filter((r) => r.price !== undefined && r.price > 0)
                .map((r) => ({
                  minWeight: r.minWeight,
                  maxWeight: r.maxWeight ?? null,
                  price: r.price,
                  currency,
                }))
            : [];

        const additionalLoadPayload = {
          transportCardRate: {
            id,
            vehicleType: data.vehicleType.value,
            transportBaseAddress: data.transportBaseAddress,
            transportBaseLocation: {
              lat: data.location.lat,
              lon: data.location.lon,
            },
            additionalChargeCurrency: currency,
            maxOffRouteDistance: data.maxOffRouteDistance,
            maxOffRouteDistanceUnit: OFF_ROUTE_DISTANCE_UNIT,
            chargePerAdditionalDrop: data.chargePerAdditionalDrop,
            standardIncludedLoadingMinutes:
              data.standardIncludedLoadingMinutes,
            standardIncludedUnloadingMinutes:
              data.standardIncludedUnloadingMinutes,
            chargePerAdditionalHour: data.chargePerAdditionalHour,
            chargePerOvertimeHour: data.chargePerOvertimeHour,
            palletRates,
            weightRates,
          },
        };
        await authHttp.post(endpoint, additionalLoadPayload);
      } else if (uploadFile) {
        // Postcode - use FormData for file upload
        const formData = new FormData();
        formData.append('Rates', uploadFile);
        formData.append('TransportBaseAddress', data.transportBaseAddress);
        formData.append('TransportBaseAddressLat', data.location.lat.toString());
        formData.append('TransportBaseAddressLon', data.location.lon.toString());
        formData.append('RateCurrency', data.currency.value);
        await authHttpFormData.post(endpoint, formData);
      }

      navigate('/transport-price-master');
    } catch (error: any) {
      console.error('Error updating tariff:', error);
      if (error?.response?.data?.errors) {
        setHasUploadError(true);
        setUploadErrors(error.response.data.errors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = ['Tariff Details'];

  const pages = [
    { name: 'Home', href: '/', current: false },
    {
      name: 'Transport Tariffs',
      href: '/transport-price-master',
      current: false,
    },
    { name: editTitle, href: `/tariff/${id}/edit`, current: true },
  ];

  // Get icon for tariff type
  const getTariffIcon = (pricingType: string) => {
    switch (pricingType) {
      case 'Postcode':
        return <MdLocationOn className='w-5 h-5' />;
      case 'Day Rate':
        return <MdAccessTime className='w-5 h-5' />;
      case 'Additional FTL':
      case 'Price Per Mile':
        return <MdLocalShipping className='w-5 h-5' />;
      default:
        return <MdLocationOn className='w-5 h-5' />;
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='flex flex-col items-center gap-3'>
          <FloxSpinner size='md' />
          <p className='text-gray-500 text-sm'>Loading tariff data...</p>
        </div>
      </div>
    );
  }

  return (
    <FormPage<TariffFormValues>
      header={editTitle}
      steps={steps}
      pages={pages}
      methods={methods}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    >
      <div className='flex flex-col gap-6 max-w-4xl'>
        {/* Tariff Type and Vehicle Type Selection */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <Controller
            name='tariffType'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                label='Tariff Type'
                options={TARIFF_TYPE_OPTIONS}
                selected={field.value}
                setSelected={field.onChange}
                isInvalid={!!error}
                validationMessage={error?.message}
                disabled
              />
            )}
          />

          <Controller
            name='vehicleType'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                label='Vehicle Type'
                options={vehicleTypeOptions}
                selected={field.value}
                setSelected={field.onChange}
                isInvalid={!!error}
                validationMessage={error?.message}
                disabled
              />
            )}
          />
        </div>

        {/* Tariff Info Banner */}
        <div className='flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
          <div className='p-2 bg-blue-100 rounded-lg'>
            {getTariffIcon(watchTariffType?.value)}
          </div>
          <div className='flex flex-col'>
            <span className='font-medium text-gray-900'>
              {watchTariffType?.label} for{' '}
              {watchVehicleType?.label || 'Selected Vehicle'}
            </span>
            <span className='text-sm text-gray-600 mt-1'>
              {watchTariffType?.value === 'Postcode' &&
                'The tariff covers prices for Point-to-Point deliveries, from the origin location in the region of the specified Transport Base to the destinations outlined in the postcode matrix below.'}
              {watchTariffType?.value === 'Additional FTL' &&
                'The tariff provides pricing for additional co-shipment deliveries, on top of an existing pre-planned route, and as per the load size matrix below.'}
              {watchTariffType?.value === 'Day Rate' &&
                'The tariff includes a charge for a full working day, starting and ending in the region of the specified Transport Base, with unlimited mileage included.'}
              {watchTariffType?.value === 'Price Per Mile' &&
                'The tariff charges a flat rate for every mile from collection to delivery. Set a minimum to guarantee a floor on short runs, and a maximum to stop the tariff being offered on collection-to-delivery journeys longer than you cover.'}
            </span>
          </div>
        </div>

        {/* Transport Base Address */}
        <Controller
          name='transportBaseAddress'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              label='Transport Base Address'
              options={addressOptions}
              value={field.value}
              onChange={(option) =>
                handleAddressSelect(
                  option as { id: string; label: string } | null
                )
              }
              onInputChange={(value) => {
                field.onChange(value);
                handleFetchSuggestions(value);
              }}
              isInvalid={!!error || !!transportBaseLocationError}
              validationMessage={error?.message ?? transportBaseLocationError}
              placeholder='Start typing an address...'
            />
          )}
        />

        {/* Currency Selection */}
        <div className='w-full md:w-1/3'>
          <Controller
            name='currency'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                label='Currency'
                options={CURRENCY_OPTIONS}
                selected={field.value}
                setSelected={field.onChange}
                isInvalid={!!error}
                validationMessage={error?.message}
              />
            )}
          />
        </div>

        {/* Day Rate Fields */}
        {watchTariffType?.value === 'Day Rate' && (
          <div className='flex flex-col gap-6'>
            <h3 className='text-lg font-semibold text-gray-900'>Day Rates</h3>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
              <Controller
                name='chargeForEightHourDay'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Charge for 8 hour day'
                    name='chargeForEightHourDay'
                    type='number'
                    placeholder='0.00'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />

              <Controller
                name='hourlyChargeOverEightHours'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Hourly charge over 8 hours'
                    name='hourlyChargeOverEightHours'
                    type='number'
                    placeholder='0.00'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />

              <Controller
                name='hourlyChargeOverTenHours'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Hourly charge over 10 hours'
                    name='hourlyChargeOverTenHours'
                    type='number'
                    placeholder='0.00'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />
            </div>
          </div>
        )}

        {/* Price Per Mile Fields */}
        {watchTariffType?.value === 'Price Per Mile' && (
          <div className='flex flex-col gap-6'>
            <h3 className='text-lg font-semibold text-gray-900'>
              Price Per Mile
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
              <Controller
                name='pricePerMile'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Price per mile'
                    name='pricePerMile'
                    type='number'
                    placeholder='0.00'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />

              <Controller
                name='baseCharge'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Base charge (optional)'
                    name='baseCharge'
                    type='number'
                    placeholder='0.00'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                    helperText='A fixed amount added once to every job, on top of the miles charged (including any minimum miles). Leave blank for none.'
                  />
                )}
              />

              <Controller
                name='maximumLoadKg'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Maximum load (kg)'
                    name='maximumLoadKg'
                    type='number'
                    placeholder='0'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                    helperText='The heaviest load this vehicle carries. Heavier loads get no quote from this rate.'
                  />
                )}
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
              <Controller
                name='fuelSurchargePercentage'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Fuel surcharge % (optional)'
                    name='fuelSurchargePercentage'
                    type='number'
                    placeholder='0'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                    helperText='A percentage added on top of the base charge and the miles charged. Leave blank for none.'
                  />
                )}
              />

              <Controller
                name='minimumMiles'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Minimum miles charged (optional)'
                    name='minimumMiles'
                    type='number'
                    placeholder='0'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />

              <Controller
                name='maximumMiles'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Maximum miles covered (optional)'
                    name='maximumMiles'
                    type='number'
                    placeholder='0'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                    helperText='The longest collection-to-delivery journey this rate quotes for. It does not limit how far you travel to collect.'
                  />
                )}
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
              <Controller
                name='effectiveFrom'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Effective from (optional)'
                    name='effectiveFrom'
                    type='date'
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value || undefined)}
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />

              <Controller
                name='effectiveTo'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Effective to (optional)'
                    name='effectiveTo'
                    type='date'
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value || undefined)}
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />
            </div>

            <Controller
              name='willCollectFromAnywhere'
              control={control}
              render={({ field }) => (
                <div className='flex flex-col gap-1'>
                  <Checkbox
                    label='Collect from anywhere'
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    name={field.name}
                    ref={field.ref}
                    aria-describedby='collect-from-anywhere-help'
                  />
                  <p
                    id='collect-from-anywhere-help'
                    className='text-sm text-gray-500'
                  >
                    Tick this if you collect at any distance from your base.
                    You&apos;re paid per mile from collection to delivery only,
                    not for travelling to the collection point. Leave it
                    unticked and this rate only quotes for collections within 50
                    km (about 30 miles) of your base.
                  </p>
                </div>
              )}
            />
          </div>
        )}

        {/* Additional Load Charge - Inline Form */}
        {watchTariffType?.value === 'Additional FTL' && (
          <LoadSizeMatrixFields
            control={control}
            watch={watch}
            rateError={
              hasUploadError
                ? uploadErrors[0]?.message || 'Please fix the errors below'
                : null
            }
          />
        )}

        {/* Postcode - File Upload */}
        {watchTariffType?.value === 'Postcode' && (
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col gap-2'>
              <h3 className='text-lg font-semibold text-gray-900'>
                Postcode Matrix
              </h3>
              <p className='text-sm text-gray-600'>
                Please use the provided template to input prices for the served
                postcode areas and per load size respectively.
              </p>
            </div>

            <div className='flex flex-col items-center gap-4 p-6 border border-gray-200 rounded-lg bg-gray-50'>
              <a
                href={`/files/${fileDownloadPath}.xlsx`}
                className='w-full max-w-md flex'
              >
                <Button
                  type='button'
                  label='Download Excel Template'
                  intent='primary'
                  startIcon={<MdDownload className='w-5 h-5' />}
                  onClick={() => {}}
                  className='flex-1'
                />
              </a>

              <span className='text-sm font-medium text-gray-500'>OR</span>

              {uploadFile ? (
                <div className='flex flex-col items-center gap-4 w-full max-w-md'>
                  <div className='flex items-center gap-2 text-gray-700'>
                    <MdUploadFile className='w-5 h-5' />
                    <span className='font-medium'>{uploadFile.name}</span>
                  </div>
                  <Button
                    type='button'
                    label='Clear Selected File'
                    intent='danger'
                    startIcon={<MdDelete className='w-5 h-5' />}
                    onClick={() => {
                      setUploadFile(null);
                      setHasUploadError(false);
                      setUploadErrors([]);
                    }}
                  />
                  {hasUploadError && (
                    <div className='flex flex-col gap-2 p-4 bg-red-50 border border-red-200 rounded-lg w-full'>
                      <span className='font-medium text-red-700'>
                        Error uploading file. Please fix the following errors:
                      </span>
                      {uploadErrors.map((error) => (
                        <span
                          key={`${error.row}-${error.message}`}
                          className='text-sm text-red-600'
                        >
                          Row {error.row}: {error.message}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className='w-full max-w-md'>
                  <Dropzone
                    label='Upload Tariff Matrix'
                    optional
                    onDrop={(file: File | null) => {
                      if (file) {
                        setUploadFile(file);
                        setValue('uploadFile', file);
                      }
                    }}
                    fileTypesTagline='Excel files (.xlsx, .xls)'
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </FormPage>
  );
}
